import { CheckboxGroup, FilterableSelect, FormLayout, Heading, Paragraph, SubmitButton } from '@expressable/syntax-ds';
import { zodResolver } from '@hookform/resolvers/zod';
import { EvaluationDuration } from 'domains/calendars/types';
import { PrimaryLanguageEnum } from 'domains/client';
import { filtersAllowForEvaluation } from 'domains/signupDirect/businessLogic/evaluationCriteria';
import { InsuranceSelect } from 'domains/signupDirect/Components/InsuranceSelect';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { ReviewsCard } from 'domains/signupDirect/EnterFiltersStep/ReviewsCard';
import { useCommunicationConcernOptions } from 'domains/signupDirect/misc/communicationConcerns';
import {
  getLanguageOptions,
  getSpecialtyOptionsAdult,
  getSpecialtyOptionsPediatric,
  getTherapyTargetOptions,
} from 'domains/signupDirect/misc/constants';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { OutcomesTiles } from 'domains/signupDirect/misc/OutcomesTiles/OutcomesTiles';
import { useSignupEvaluationCalendar } from 'domains/signupDirect/misc/useSignupEvaluationCalendar';
import { useSignupNavigationGuard } from 'domains/signupDirect/misc/useSignupNavigationGuard';
import { DirectSignupFiltersFormType, directSignupFiltersSchema } from 'domains/signupDirect/schemas/filtersSchema';
import { merge } from 'lodash';
import { DefaultValues, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAutoScrollToTop } from 'utils/hooks/useAutoScrollToTop/useAutoScrollToTop';
import { useTrack } from 'utils/hooks/useMixPanel';
import { StateAbbrev, stateOptions } from 'utils/states';
import { filtersAllowSplitEvaluation } from '../businessLogic/splitEvaluationCriteria';

export const DirectSignupEnterFilters = () => {
  const { filters, incompleteFiltersForm, isResumedLead, onSubmitFilters } = useSignupDirectCtx();

  useTrack(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.FILTERS, { isResumedLead });

  const methods = useForm<DirectSignupFiltersFormType>({
    resolver: zodResolver(directSignupFiltersSchema),
    defaultValues: merge(
      {
        language: PrimaryLanguageEnum.English,
        paymentMethod: undefined,
      } satisfies DefaultValues<DirectSignupFiltersFormType>,
      incompleteFiltersForm,
      filters,
    ),
  });

  const { handleSubmit } = methods;

  const { t } = useTranslation();
  useAutoScrollToTop();

  return (
    <>
      <div>
        <Heading level="h1">{t('directSignup.sections.filters.title')}</Heading>
        <Paragraph size="lg">
          {t('directSignup.sections.filters.subtitle.1')}
          <strong>{t('directSignup.sections.filters.subtitle.2')}</strong>
        </Paragraph>
      </div>

      <FormProvider {...methods}>
        <FormLayout
          className="mobile-xs-d:gap-e40 tablet-d:gap-e56"
          onSubmit={handleSubmit(data => {
            onSubmitFilters(data);
          })}
        >
          <FormLayout.Fields className="mobile-xs-d:gap-e24 tablet-d:gap-e32">
            <FormFields />
          </FormLayout.Fields>
          <FormLayout.CTA className="w-full">
            <SubmitButton>{t('directSignup.filters.submit')}</SubmitButton>
          </FormLayout.CTA>
          <OutcomesTiles />
        </FormLayout>
      </FormProvider>
      <ReviewsCard />
    </>
  );
};

const FormFields = () => {
  const { t } = useTranslation();
  const { setValue } = useFormContext<DirectSignupFiltersFormType>();
  const { isResumedLead } = useSignupDirectCtx();

  const therapyTarget = useWatch<Partial<DirectSignupFiltersFormType>, 'therapyTarget'>({ name: 'therapyTarget' });
  const selectedState = useWatch<Partial<DirectSignupFiltersFormType>, 'stateCode'>({ name: 'stateCode' });
  const clinicalConcernKeys = useWatch<Partial<DirectSignupFiltersFormType>, 'clinicalConcernKeys'>({
    name: 'clinicalConcernKeys',
  });

  const paymentMethod = useWatch<Partial<DirectSignupFiltersFormType>, 'paymentMethod'>({ name: 'paymentMethod' });

  const language = useWatch<Partial<DirectSignupFiltersFormType>, 'language'>({ name: 'language' });

  const fullConcerns = useCommunicationConcernOptions({ currentKeys: clinicalConcernKeys, therapyTarget });

  const therapyTargetOptions = getTherapyTargetOptions(t);
  const concernsChild = getSpecialtyOptionsPediatric(t, isResumedLead);
  const concernsAdult = getSpecialtyOptionsAdult(t, isResumedLead);
  const languageOptions = getLanguageOptions();

  const { allowed: canPickEval } = filtersAllowForEvaluation({
    paymentMethod,
    target: therapyTarget,
    clinicalConcerns: fullConcerns,
    language,
    isResumedLead,
  });

  const { allowed: canPickSplitEval } = filtersAllowSplitEvaluation({
    paymentMethod,
  });

  useSignupNavigationGuard();

  return (
    <>
      {selectedState &&
        therapyTarget &&
        clinicalConcernKeys &&
        paymentMethod &&
        paymentMethod.type !== 'otherInsurance' &&
        canPickEval && (
          <CalendarPreLoader
            state={selectedState as StateAbbrev}
            clinicalConcernKeys={clinicalConcernKeys}
            paymentMethod={paymentMethod}
            therapyTarget={therapyTarget}
            allowSplitEvaluation={canPickSplitEval}
          />
        )}

      <FilterableSelect
        data-testid="therapy-target-select"
        label={t('directSignup.filters.therapyTarget')}
        options={therapyTargetOptions}
        name="therapyTarget"
        onChange={() => setValue('clinicalConcernKeys', [])}
      />

      {therapyTarget && (
        <CheckboxGroup
          variant="plain"
          id="clinicalConcernKeys"
          name="clinicalConcernKeys"
          label={t('directSignup.filters.clinicalConcernKeys')}
          options={therapyTarget === 'child' ? concernsChild : concernsAdult}
        />
      )}

      <FilterableSelect
        data-testid="state-select"
        label={t('directSignup.filters.state')}
        options={stateOptions}
        name="stateCode"
        // @ts-expect-error workaround to reset this field, not sure if there's a better way
        onChange={() => setValue('paymentMethod', null)}
      />

      <FilterableSelect
        data-testid="language-select"
        label={t('directSignup.filters.language')}
        options={languageOptions}
        name="language"
      />

      <InsuranceSelect state={selectedState} />
    </>
  );
};

/**
 * Note(juan):
 * this is a quick tricky way of getting calendar data to start pre-fetching as soon as possible,
 * since this call can take a good couple of seconds. We don't actually need eval calendar here
 *
 * Contained inside a component as a way of conditionally calling the hook (when state is selected)
 */
const CalendarPreLoader = ({
  state,
  therapyTarget,
  paymentMethod,
  clinicalConcernKeys,
  allowSplitEvaluation,
}: {
  state: StateAbbrev;
  paymentMethod: DirectSignupFiltersFormType['paymentMethod'];
  clinicalConcernKeys: DirectSignupFiltersFormType['clinicalConcernKeys'];
  therapyTarget: DirectSignupFiltersFormType['therapyTarget'];
  allowSplitEvaluation: boolean;
}) => {
  useSignupEvaluationCalendar(
    {
      clinicalConcernKeys,
      paymentMethod,
      stateCode: state,
      therapyTarget,
    },
    allowSplitEvaluation ? EvaluationDuration.Split : EvaluationDuration.Single,
  );

  return null;
};
